// Check-in desk of the event day (docs/processes/09): search by name, email, ticket number or company; one big «Пропустить».

import { useEffect, useMemo, useState } from 'react';
import { textKey, type RecordObj } from '@confdeck/core-shared';
import { Button, Chip, Input, SearchField, TopBar, TopTitle, cx, recordTitle, useMeta, useNarrow, useScope, useSync, useToast, useTypesTick } from '@confdeck/core-web';
import s from './conferences.module.css';

export function CheckIn() {
  const client = useSync();
  const meta = useMeta();
  const toast = useToast();
  const [eventId, setScope] = useScope();
  // The QR of a ticket in the personal page opens this screen with «?ticket=<номер>» (J3): the ticket is found in any event.
  const [q, setQ] = useState(() => new URLSearchParams(location.search).get('ticket') ?? '');
  const narrow = useNarrow();
  // The desk is a device at the door: its name lives in this browser and goes into the log of passes with every pass (process 09).
  const [desk, setDesk] = useState(() => { try { return localStorage.getItem('cd.checkin.desk') ?? ''; } catch { return ''; } });
  const saveDesk = (v: string) => { setDesk(v); try { localStorage.setItem('cd.checkin.desk', v); } catch { /* private mode: kept for this visit */ } };
  const ticketType = meta.typeByKey('ticket');
  useTypesTick(ticketType ? [ticketType.id] : []);
  const eventField = ticketType ? meta.field(ticketType.id, 'event') : undefined;
  const statusField = ticketType ? meta.field(ticketType.id, 'status') : undefined;
  // The door lets in only a ticket with a seat (ticket-gives.ts, the same rule as «ticket.checkIn»): the recordings bought after the
  // event or an online offer are not in the plan of the door and get no «Пропустить». The system marks such a ticket «Без места в
  // зале», so the desk reads the ticket alone — a change of what an offer gives rewrites its tickets, and the desk follows them.
  const seat = (t: RecordObj) => !t.data.no_seat;
  const active = statusField?.options.taxonomyId ? meta.terms(statusField.options.taxonomyId).find((t) => t.final === 'won')?.id : undefined;

  const tickets = useMemo(() => {
    if (!ticketType || !eventField || !eventId) return [] as RecordObj[];
    const out: RecordObj[] = [];
    for (const rid of client.store.recordIds(ticketType.id)) {
      const r = client.store.record(rid);
      if (r && client.store.linked(r, eventField).includes(eventId)) out.push(r);
    }
    return out;
  }, [ticketType, eventField, eventId, client.store, client.store.typeVersion.get(ticketType?.id ?? '')]);

  // The scanned ticket belongs to another event than the chosen one: the desk switches to its event.
  useEffect(() => {
    const num = new URLSearchParams(location.search).get('ticket');
    if (!num || !ticketType || !eventField) return;
    for (const rid of client.store.recordIds(ticketType.id)) {
      const r = client.store.record(rid);
      if (r && String(r.data.number) === num) { const ev = client.store.linked(r, eventField)[0]; if (ev && ev !== eventId) setScope(ev); return; }
    }
  }, [ticketType, eventField, client.store, client.store.typeVersion.get(ticketType?.id ?? '')]);

  const valid = tickets.filter((t) => t.data.status === active && t.data.email && seat(t));
  const passed = valid.filter((t) => t.data.checked_in_at).length;
  const k = textKey(q);
  const found = k.length >= 2 ? tickets.filter((t) => [t.data.name, t.data.email, t.data.number, t.data.company_name].some((v) => textKey(v ?? '').includes(k))).slice(0, 30) : [];

  const pass = async (t: RecordObj) => {
    const r = await client.run('ticket.checkIn', { id: t.id, ...(desk.trim() ? { desk: desk.trim() } : {}) });
    if (r.ok) { toast({ kind: 'info', text: `${t.data.name ?? 'Участник'} прошёл` }); setQ(''); }
    else toast({ kind: 'error', text: r.error.message });
  };

  if (!eventId) return <div className={s.empty}>Выберите событие внизу левой колонки.</div>;
  const event = client.store.record(eventId);
  return (
    <div className={s.screen}>
    <TopBar start={<TopTitle icon="tick" note={recordTitle(event, meta)}>Вход на событие</TopTitle>} count={`${passed} из ${valid.length} прошли`}
      end={<Input className={s.deskName} value={desk} placeholder="Стойка, например «Вход А»" aria-label="Название стойки" onChange={(e) => saveDesk(e.target.value)} />} />
    <div className={s.desk}>
      <SearchField size="lg" autoFocus className={s.deskSearch} value={q} onChange={setQ} placeholder={narrow ? 'Фамилия, email или билет' : 'Фамилия, email, номер билета или компания'} label="Поиск участника"
        onKeyDown={(e) => { if (e.key === 'Enter' && found.length === 1) void pass(found[0]!); }} />
      <div className={s.deskList}>
        {found.map((t) => {
          const st = meta.term(String(t.data.status));
          const ok = t.data.status === active && t.data.email && !t.data.checked_in_at && seat(t);
          const reason = !seat(t) ? 'Без входа: в варианте нет «Площадки»' : t.data.checked_in_at ? `Уже прошёл в ${new Date(String(t.data.checked_in_at)).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
            : t.data.status !== active ? (st?.final === 'lost' ? 'Билет аннулирован' : 'Билет не оплачен') : !t.data.email ? 'Место не назначено' : null;
          return (
            <div key={t.id} className={cx(s.person, !ok && s.personNo)}>
              <div className={s.personMain}>
                <div className={s.personName}>{String(t.data.name ?? 'Место не назначено')}</div>
                <div className={s.personMeta}>{[t.data.email, t.data.company_name, `№ ${t.data.number}`].filter(Boolean).join(' · ')}</div>
              </div>
              {st && <Chip color={st.color} dot>{st.name}</Chip>}
              {ok ? <Button variant="primary" size="lg" icon="tick" onClick={() => void pass(t)}>Пропустить</Button> : <span className={s.reason}>{reason}</span>}
            </div>
          );
        })}
        {k.length >= 2 && !found.length && <div className={s.empty}>Никого не нашли. Проверьте написание или номер билета.</div>}
        {k.length < 2 && <div className={s.hint}>Начните вводить — достаточно двух букв. Enter пропускает, если найден один человек.</div>}
      </div>
    </div>
    </div>
  );
}
